import React from 'react';
import { Link } from 'react-router-dom';
import { EventData, RecentEvent } from '../../types/signal';
import { getEventPagePath } from '../../utils/eventPaths';

interface CatalystSidebarProps {
  label: string;
  event: EventData | null;
  recentEvents: RecentEvent[];
  ticker: string;
}

export default function CatalystSidebar({ label, event, recentEvents, ticker }: CatalystSidebarProps) {
  const isCatalyst = label === 'catalyst' || label === 'catalyst-backed';
  const isNone = label === 'none';

  return (
    <div className="flex flex-col gap-4 font-sans">
      {event && (
        <div className={`bg-white dark:bg-cat-surface rounded-2xl p-5 border shadow-sm ${
          isCatalyst ? 'border-cat-green/40' : isNone ? 'border-cat-red/40' : 'border-gray-200 dark:border-cat-border'
        }`}>
          <div className="flex justify-between mb-4 text-[11px]">
            <span className={`font-bold uppercase tracking-wider ${
              isCatalyst ? 'text-emerald-700 dark:text-cat-green' : isNone ? 'text-cat-red' : 'text-gray-500 dark:text-cat-text-muted'
            }`}>
              {isNone ? 'Service unreachable' : 'Matched catalyst'}
            </span>
            <span className="text-gray-500 dark:text-cat-text-muted font-mono">{event.time_rel}</span>
          </div>

          {label === 'momentum' && (
            <p className="text-gray-500 dark:text-cat-text-muted m-0 text-[13px] leading-relaxed">
              No catalyst matched for this technical breakout. Momentum driven movement.
            </p>
          )}

          {label === 'none' && (
            <p className="text-red-600 dark:text-cat-red m-0 text-[13px] leading-relaxed">
              Catalyst validation unavailable. Downstream events architecture service is offline.
            </p>
          )}

          {isCatalyst && (
            <div>
              <h4 className="text-gray-900 dark:text-cat-text-main m-0 mb-2.5 text-[15px] font-bold leading-snug">
                {event.title}
              </h4>
              <p className="text-gray-500 dark:text-cat-text-muted text-[12px] leading-relaxed mb-4">
                {event.summary}
              </p>
              <Link
                to={getEventPagePath(event.id)}
                className="text-cat-accent no-underline text-[12px] font-semibold hover:underline"
              >
                Read article →
              </Link>
            </div>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-cat-surface border border-gray-200 dark:border-cat-border rounded-2xl p-5 shadow-sm">
        <h4 className="text-gray-500 dark:text-cat-text-muted text-[11px] m-0 mb-4 font-bold uppercase tracking-wider">
          Recent {ticker} events <span className="font-normal normal-case tracking-normal">· last 24h</span>
        </h4>
        
        {recentEvents.length === 0 ? (
          <p className="text-gray-500 dark:text-cat-text-muted text-[12px] m-0">No recent events recorded.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {recentEvents.map((item, index) => {
              const content = (
                <div className={`flex gap-4 text-[12px] pb-2.5 ${index < recentEvents.length - 1 ? 'border-b border-gray-100 dark:border-cat-border' : ''}`}>
                  <span className="text-gray-500 dark:text-cat-text-muted shrink-0 font-mono">{item.time}</span>
                  <div>
                    <div className="text-gray-800 dark:text-[#d5deea] mb-0.5 group-hover:text-cat-accent transition-colors">{item.title}</div>
                    <div className="text-gray-500 dark:text-cat-text-muted text-[10px] uppercase tracking-wider">{item.source}</div>
                  </div>
                </div>
              );

              if (item.id) {
                return (
                  <Link
                    key={item.id}
                    to={getEventPagePath(item.id)}
                    className="block cursor-pointer group no-underline"
                  >
                    {content}
                  </Link>
                );
              }

              if (item.link) {
                return (
                  <a 
                    key={index} 
                    href={item.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block cursor-pointer group no-underline"
                  >
                    {content}
                  </a>
                );
              }

              return (
                <div key={index} className="group">
                  {content}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
